import * as React from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const initialForm = {
  schema_id: '',
  version: '',
  title: '',
  description: '',
  scope: 'global',
  type: '',
  subtype: '',
  is_amx_owned: true,
  owning_partner_id: '',
  body: '[]',
  metadata: '{}',
};

export default function AdminSharedSchemasPage(props) {
  const [state, setState] = useState({ loading: true, error: '', schemas: [], registryStatus: 'available', registryError: '' });
  const [form, setForm] = useState(initialForm);
  const [feedback, setFeedback] = useState('');

  async function loadSchemas() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/shared-schemas');
      setState({
        loading: false,
        error: '',
        schemas: Array.isArray(response.data?.schemas) ? response.data.schemas : [],
        registryStatus: response.data?.registry_status || 'available',
        registryError: response.data?.registry_error || '',
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error?.response?.data?.message || 'Unable to load shared schemas.',
      }));
    }
  }

  useEffect(() => {
    loadSchemas();
  }, []);

  async function handleCreate(event) {
    event.preventDefault();
    setFeedback('');

    let parsedBody = [];
    let parsedMetadata = {};
    try {
      parsedBody = JSON.parse(form.body || '[]');
      parsedMetadata = JSON.parse(form.metadata || '{}');
    } catch (error) {
      setState((current) => ({ ...current, error: 'Schema body and metadata must be valid JSON.' }));
      return;
    }

    try {
      await axiosInstance.post('/admin/shared-schemas', {
        schema_id: form.schema_id,
        version: form.version,
        title: form.title,
        description: form.description,
        scope: form.scope,
        type: form.type,
        subtype: form.subtype,
        is_amx_owned: form.is_amx_owned,
        owning_partner_id: form.owning_partner_id,
        body: parsedBody,
        metadata: parsedMetadata,
      });
      setForm(initialForm);
      setFeedback('Shared schema created.');
      await loadSchemas();
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error?.response?.data?.message || 'Unable to create shared schema.',
      }));
    }
  }

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard', href: '/admin' }, { label: 'Schema Registry' }]}
      title="Schema Registry"
      subtitle="Advanced registry administration for inspecting, importing, and debugging schema versions."
    >
      {feedback ? <Alert severity="success">{feedback}</Alert> : null}
      {state.error ? <Alert severity="error">{state.error}</Alert> : null}
      {state.registryStatus === 'unavailable' ? (
        <Alert severity="warning">
          Schema registry is unavailable. Existing registry entries cannot be loaded right now.
          {state.registryError ? ` ${state.registryError}` : ''}
        </Alert>
      ) : null}

      <AdminSection title="Register Legacy Schema Version" subtitle="Advanced JSON registration for registry maintenance and controlled imports.">
        <Stack component="form" spacing={2} onSubmit={handleCreate}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Schema ID" value={form.schema_id} onChange={(event) => setForm({ ...form, schema_id: event.target.value })} fullWidth required />
            <TextField label="Version" value={form.version} onChange={(event) => setForm({ ...form, version: event.target.value })} fullWidth required />
            <TextField label="Title" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} fullWidth required />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField select label="Scope" value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value })} fullWidth>
              <MenuItem value="global">global</MenuItem>
              <MenuItem value="p2p">p2p</MenuItem>
              <MenuItem value="local">local</MenuItem>
            </TextField>
            <TextField label="Type" value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })} fullWidth placeholder="exchange_card" />
            <TextField label="Subtype" value={form.subtype} onChange={(event) => setForm({ ...form, subtype: event.target.value })} fullWidth placeholder="verify_printer_readiness" />
            <TextField select label="Ownership" value={form.is_amx_owned ? 'amx' : 'partner'} onChange={(event) => setForm({ ...form, is_amx_owned: event.target.value === 'amx' })} fullWidth>
              <MenuItem value="amx">AMX owned</MenuItem>
              <MenuItem value="partner">Partner owned</MenuItem>
            </TextField>
          </Stack>
          <TextField label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} fullWidth multiline minRows={2} />
          <TextField label="Body JSON" value={form.body} onChange={(event) => setForm({ ...form, body: event.target.value })} fullWidth multiline minRows={4} />
          <TextField label="Metadata JSON" value={form.metadata} onChange={(event) => setForm({ ...form, metadata: event.target.value })} fullWidth multiline minRows={3} />
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained">Create Registry Entry</Button>
          </Stack>
        </Stack>
      </AdminSection>

      <AdminSection title="Schema Registry" subtitle="Current shared schema versions visible to the AMX admin group.">
        {state.loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <span>Loading shared schemas...</span>
          </Stack>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Schema</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Fields</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.schemas.map((schema) => (
                <TableRow key={`${schema.schema_id}-${schema.version}`} hover>
                  <TableCell>{schema.title || schema.schema_id}</TableCell>
                  <TableCell>{schema.version}</TableCell>
                  <TableCell>{schema.scope}</TableCell>
                  <TableCell>{[schema.type, schema.subtype].filter(Boolean).join(' / ') || '-'}</TableCell>
                  <TableCell>{schema.is_amx_owned ? 'AMX' : schema.owning_partner_id || '-'}</TableCell>
                  <TableCell>{Array.isArray(schema.body) ? schema.body.length : 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminSection>
    </AdminLayout>
  );
}
